import ky from 'ky';
import { map } from 'lodash';
import { useState } from 'react';
import { useEnvProvider } from '~/hooks';
import {
  FileUploadHookProps,
  UploadedFile,
  FileUploadProps,
  FileUploadResponse,
  OnDrop,
  FileUploadError,
} from './types';

export const useFileUpload = (
  onSuccess: FileUploadProps['onSuccess'],
  onFileUploadError: FileUploadProps['onError'],
  maxFileSizeMB: FileUploadProps['maxFileSizeMB'] = undefined,
  acceptFileTypes: FileUploadProps['acceptFileTypes']
): [OnDrop, FileUploadHookProps] => {
  const { envConfig } = useEnvProvider();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<UploadedFile[]>([]);

  return [
    async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles && rejectedFiles.length > 0 && acceptFileTypes && acceptFileTypes.length > 0) {
        const rejectedFileTypes = map(rejectedFiles, 'type');
        const isRejected = !rejectedFileTypes.some((rejectedFileTypeItem) =>
          Array.isArray(acceptFileTypes)
            ? acceptFileTypes.includes(rejectedFileTypeItem)
            : acceptFileTypes === rejectedFileTypeItem
        );
        if (isRejected && onFileUploadError) {
          onFileUploadError(FileUploadError.FILE_TYPE_NOT_SUPPORTED);
          setLoading(false);
          return;
        }
      }

      if (acceptedFiles.length > 1 && onFileUploadError) {
        onFileUploadError(FileUploadError.EXCEED_MAX_FILE_LIMIT);
        setLoading(false);
        return;
      }

      const [file] = acceptedFiles;
      if (maxFileSizeMB) {
        const fileSizeMB = file.size / 1024 ** 2;
        if (fileSizeMB > maxFileSizeMB && onFileUploadError) {
          onFileUploadError(FileUploadError.EXCEED_MAX_FILE_SIZE_LIMIT);
          setLoading(false);
          return;
        }
      }
      const bodyFormData = new FormData();
      bodyFormData.append('mediafile', file);

      setLoading(true);

      try {
        const response: FileUploadResponse = await ky
          .post(envConfig.mediaServiceUrl + '/upload' || '', {
            body: bodyFormData,
            credentials: 'include',
            retry: {
              limit: 3,
              statusCodes: [413, 429, 404],
            },
            timeout: 25000,
          })
          .json();

        setLoading(false);

        setFiles((prev) => {
          const augmentedFile = { ...file, preview: URL.createObjectURL(file), attachmentId: response.attachmentId };
          return [...prev, augmentedFile];
        });

        onSuccess(response, file);
      } catch (error) {
        setLoading(false);
        if (
          error &&
          onFileUploadError &&
          error.message.replace(/\s+/g, '_').toUpperCase() == FileUploadError.REQUEST_TIMED_OUT
        ) {
          onFileUploadError(FileUploadError.REQUEST_TIMED_OUT);
          return;
        }
        if (error && onFileUploadError) {
          onFileUploadError(error);
        }
      }
    },
    { loading, files, setFiles },
  ];
};
